import * as React from 'react';
import { motion } from 'framer-motion';
import { graphql, Link } from 'gatsby';
import {
  GatsbyImage,
  type IGatsbyImageData,
  StaticImage
} from 'gatsby-plugin-image';
import moment from 'moment';
import { rgba } from 'polished';
import styled from 'styled-components';

import {
  Banner,
  Card,
  Heading,
  Text,
  Voronoi
} from '../components/design-system';
import { Page } from '../components/global/Page';
import { ArrowLeftIcon } from '../icons/Icons';
import { ANIMATIONS, INTERNAL_LINKS } from '../utils';

const Author = styled(Text)`
  opacity: 0.8;
  margin: 0px 0px 6px;
`;
const Date = styled(Text)`
  opacity: 0.8;
  margin: 0px 0px 6px;
`;
const BreadCrumbs = styled.ul`
  margin: 0 0 20px 0;
  padfding: 0;
  display: flex;
  felx-direction: row;
  align-items: center;
  justify-content: left;
  list-style: none;
`;

const BreadCrumbsItems = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0;
  span {
    color: ${({ theme }) => rgba(theme.colors.WHITE, 1)};
  }
  &:nth-child(odd) {
    &::after {
      content: '/';
      display: inline-block;
      margin: 0 10px;
      color: ${({ theme }) => rgba(theme.colors.LIGHT_PINK, 0.75)};
    }
  }
`;
const Icon = styled(ArrowLeftIcon)`
  color: ${({ theme }) => rgba(theme.colors.LIGHT_PINK, 0.75)};
  margin-right: 10px;
`;
const Wrapper = styled(motion.div)`
  width: 100%;
`;
const GradientHeader = styled(Heading).attrs({ renderAs: 'h4' })`
  margin-bottom: 6px;
`;

const ItemWrapper = styled(motion.div)`
  display: flex;
  background: ${({ theme }) => theme.gradientFade};
  border-top: 1px solid ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.6)};
  padding: 100px 40px 120px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 80px 40px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    padding: 60px 30px;
  }
`;
const WrapperItems = styled(motion.div)`
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  padding: 0 30px;
  margin-bottom: 100px;
`;
const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const BreadCrumbsNoSlash = styled(BreadCrumbsItems)`
  &:nth-child(odd) {
    &::after {
      display: none;
    }
  }
`;
const SubText = styled(Text)`
  font-size: 20px;
  line-height: 30px;
  max-width: 540px;
`;

const StyledHeading = styled(Heading)`
  margin-bottom: 15px;
`;

const StyledInfo = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  flex-direction: column;
  padding: 15px 5px 5px;
`;
const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 50px;
  max-width: 700px;

  p {
    margin-top: 1.25rem;
    max-width: 450px;
    text-align: center;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin-bottom: 30px;
  }
`;
const HeroWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  padding: 0 30px;
`;
const HeroColumn = styled.div`
  flex: 1;
  width: 100%;
  margin-bottom: 70px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    max-width: 60%;
  }
`;
const GridItemsWrapper = styled(Card)`
  flex-direction: column;
`;

const GridItems = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 20px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    grid-template-columns: 1fr;
  }
`;
const ItemLink = styled(Link)`
  border: none;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.WHITE};
  &:hover {
    background-color: transparent;
  }
`;
const ItemInner = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
`;
const StyledTop = styled(Top)`
  margin-bottom: 70px;
`;
const StyledPreviewImage = styled.div`
  width: 100%;
  height: auto;
  margin-bottom: 0;
  border: none;
  position: relative;
  flex: none;
  overflow: hidden;
`;
const PreviewText = styled(Text)`
  position: relative;
  overflow: hidden;
  max-height: 75px;
  width: 100%;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;
const SectionHeading = styled(Heading)`
  text-align: center;
`;

interface UseCasesTags {
  description: string;
  id: string;
  internal: {
    type: string;
  };
  seoMetaDescription: {
    seoMetaDescription: string;
  };
  seoTitle: string;
  slug: string;
  socialSharePreviewImage: {
    title: string;
    url: string;
  };
  title: string;
}
interface UseCasePosts {
  author?: string;
  category: {
    slug: string;
  };
  categorySlug: string;
  description: {
    description: string;
  };
  gridImage: {
    gatsbyImageData: IGatsbyImageData;
    title: string;
  };
  id: string;
  internal: {
    type: string;
  };
  name: string;
  slug: string;
  socialSharePreviewImage: {
    title: string;
    url: string;
  };
  tagLinks?: {
    id: string;
    slug: string;
    title: string;
  }[];
  title: string;
  updatedAt: string;
}
interface UseCaseTagTemplateProps {
  data: {
    otherTags: {
      edges: {
        node: UseCasesTags;
      }[];
    };
    route: UseCasesTags;
    useCasePost: {
      edges: {
        node: UseCasePosts;
      }[];
    };
  };
}

const UseCaseTagTemplate: React.ComponentType<UseCaseTagTemplateProps> =
  React.memo(({ data }) => {
    const {
      seoTitle,
      slug,
      seoMetaDescription,
      description,
      socialSharePreviewImage,
      title
    } = data.route;

    const currentSlug = slug;
    const otherTags = data.otherTags.edges;
    const useCasePosts = data.useCasePost.edges;

    //Check if there is a grid image, if not, use default
    // @ts-expect-error todo
    const getImageComponent = node => {
      if (node.gridImage && node.gridImage.gatsbyImageData) {
        return (
          <GatsbyImage
            alt={node.gridImage.title}
            image={node.gridImage.gatsbyImageData}
          />
        );
      } else if (node.internal.type === 'ContentfulUseCase') {
        return (
          <StaticImage
            alt="Fallback Description"
            src="../images/fallback/use-case-grid-image-fallback-image.jpg"
          />
        );
      } else {
        return (
          <StaticImage
            alt="Fallback Description"
            src="../images/fallback/use-case-tags-templates-grid-image-fallback-image.jpg"
          />
        );
      }
    };
    //Format date
    const formattedDate = (date: string) => {
      return moment(date).format('MMMM D, YYYY');
    };
    return (
      <Page
        seoProps={{
          title: seoTitle || title,
          description: seoMetaDescription
            ? seoMetaDescription.seoMetaDescription
            : description,
          image: socialSharePreviewImage ? socialSharePreviewImage.url : '',
          url: `${INTERNAL_LINKS.Templates}use-cases/${currentSlug}/${slug}/`
        }}>
        <Voronoi />
        <Wrapper {...ANIMATIONS.SimpleFadeIn}>
          <Inner>
            <HeroWrapper>
              <HeroColumn>
                <BreadCrumbs>
                  <BreadCrumbsNoSlash>
                    <Banner to={INTERNAL_LINKS.Templates}>
                      <Icon />
                      <span>Use cases</span>
                    </Banner>
                  </BreadCrumbsNoSlash>
                </BreadCrumbs>
                <StyledHeading renderAs="h1">{title} templates</StyledHeading>
                <SubText>{description}</SubText>
              </HeroColumn>
            </HeroWrapper>
            {useCasePosts && (
              <WrapperItems>
                <GridItems>
                  {useCasePosts
                    .filter(({ node }) =>
                      node.tagLinks?.some(tag => tag.slug === currentSlug)
                    )
                    .map(({ node }) => {
                      return (
                        <GridItemsWrapper key={node.id}>
                          <ItemLink
                            to={`${INTERNAL_LINKS.Templates}${node.category.slug}/${node.slug}/`}>
                            <StyledPreviewImage>
                              {getImageComponent(node)}
                            </StyledPreviewImage>
                            <StyledInfo>
                              {/* @ts-expect-error todo */}
                              <GradientHeader>{node.name}</GradientHeader>
                              {node.author ? (
                                <Author>{node.author}</Author>
                              ) : (
                                <Date>{formattedDate(node.updatedAt)}</Date>
                              )}
                              <PreviewText>
                                {node.description.description}
                              </PreviewText>
                            </StyledInfo>
                          </ItemLink>
                        </GridItemsWrapper>
                      );
                    })}
                </GridItems>
              </WrapperItems>
            )}
            <ItemWrapper>
              <ItemInner>
                <StyledTop>
                  <SectionHeading renderAs="h2">
                    See what else Hex can do
                  </SectionHeading>
                  <SubText>
                    Discover how Hex data scientists and analysts use Hex for
                    everything from dashboards to deep dives.
                  </SubText>
                </StyledTop>
                <GridItems>
                  {otherTags
                    .filter(({ node }) => node.slug !== currentSlug)
                    .slice(0, 6) // Limit to 6
                    .map(({ node }) => (
                      <GridItemsWrapper key={node.id}>
                        <ItemLink
                          to={`${INTERNAL_LINKS.Templates}tag/${node.slug}/`}>
                          <StyledPreviewImage>
                            {getImageComponent(node)}
                          </StyledPreviewImage>
                          <StyledInfo>
                            {/* @ts-expect-error todo */}
                            <GradientHeader>
                              {node.title} templates
                            </GradientHeader>
                            <PreviewText>{node.description}</PreviewText>
                          </StyledInfo>
                        </ItemLink>
                      </GridItemsWrapper>
                    ))}
                </GridItems>
              </ItemInner>
            </ItemWrapper>
          </Inner>
        </Wrapper>
      </Page>
    );
  });

export const pageQuery = graphql`
  query ($slug: String!) {
    route: contentfulUseCaseTags(slug: { eq: $slug }) {
      ...BlockContentfulUseCasesTags
    }
    otherTags: allContentfulUseCaseTags {
      edges {
        node {
          ...BlockContentfulUseCasesTags
        }
      }
    }
    useCasePost: allContentfulUseCase {
      edges {
        node {
          ...BlockContentfulUseCase
        }
      }
    }
  }
`;

export default UseCaseTagTemplate;
